import AdditionalFields, {field} from "@/core/misc/decorators/AdditionalFields";
import Title_ru_RU from "@/core/misc/decorators/Title_ru_RU";
import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";
import serializeSimplified from "@/core/misc/decorators/SerializeSimplified";
import TYPES from '@/core/services/Changelogs/types';
import Core from "@/core/Core";
import TenderBid from '@/domains/TenderBids/TenderBid';
import PaymentTerm from '@/domains/PaymentTerms/PaymentTerm';
import AbstractDemand from "@/domains/Abstract/AbstractDemand";
import DateSimple from '@/core/types/DateSimple';
import TimeStamp from '@/core/types/TimeStamp';
import TendersService from '@/domains/Tenders/TendersService';
import Demand from "@/domains/Demand/Demand";
import TenderParticipationRequest from "@/domains/TenderParticipationRequest/TenderParticipationRequest";
import TenderParticipationRequestsService from "@/domains/TenderParticipationRequest/TenderParticipationRequestsService";
import ParticipationDetailProduct
  from "@/domains/ParticipationDetailProduct/ParticipationDetailProduct";
import FeedRecord from '@/core/services/Feeds/FeedRecord';
import PaymentTermsService from '@/domains/PaymentTerms/PaymentTermsService';
import Price from "@/core/types/Price";
import Qty from "@/core/types/Qty";
import WinnersCategory from "@/domains/Tenders/WinnersCategory";
import OrderPosition from "@/domains/Orders/OrderPosition";
import PreorderPosition from "@/domains/Preorders/PreorderPosition";
import TenderSuppliersStats from "@/domains/Tenders/TenderSuppliersStats";

@AdditionalFields({
  priceFinished: field('лучшая цена', TYPES.PRICE),
  priceFullBuyerWithDeferredPayment: field('лучшая цена с отсрочкой', TYPES.PRICE),
  plannedDate: field('дата поставки', TYPES.DATE),
  currencyCode: field('валюта', TYPES.STRING),
  isRunning: field('активная фаза', TYPES.BOOLEAN),
  isProcessed: field('обработанность', TYPES.BOOLEAN_MARK),
  /** пережиток прошлого, на момент 22.11.2024 уже не актуальны, добавлены для обратной совместимости */
  startDate: field('начало торгов', TYPES.DATE_TIME),
  endDate: field('конец торгов', TYPES.DATE_TIME),
  priceStartOrigin: field('начальная цена', TYPES.PRICE),
  hasOrder: field('заказ', TYPES.BOOLEAN_MARK),
  refusalReasons: field('причины отказа', TYPES.STRING),
  refusalReasonCustomText: field('специфическая причина отказа', TYPES.STRING),
  supplyOffer: field('разрешение на поставку', TYPES.OBJECT, (oldValue: any, newValue: any): string => {
    if (newValue?.seller && newValue?.status) {
      return `для ${newValue.seller.name} на ${newValue.status.id}`;
    }
    return '--'
  }),
})
@Title_ru_RU_Class('Тендер')                        // tender → Tender
class Tender extends AbstractDemand {
  /**
   * Общие для пачки поля: при массовом создании форма заполняется на одном
   * тендере, а перед отправкой значения раскладываются по остальным.
   * Всё, чего здесь нет — своё у каждого: товар, потребность, количество,
   * стартовая цена и её валюта.
   */
  static readonly SHARED_FIELDS = [
    'buyerCompany',
    'buyerStore',
    'buyerProductionSite',
    'responsibleBuyer',
    'winnersCategory',
    'startAt',
    'endAt',
    'isCreationDelayMode',
  ] as const;

  /** Юра решил что надо */
  @Title_ru_RU('пометка избранного', TYPES.BOOLEAN)
  isBookmarked: boolean

  /** Создан ли заказ? */
  @Title_ru_RU('заказ', TYPES.BOOLEAN_MARK)
  isOrdersCreated: boolean

  @Title_ru_RU('схема распределения победителей', TYPES.OBJECT)
  /** Говорит о том сколько может быть победителей и в какой мере (%) они удовлетворяют потребность */
  winnersCategory: WinnersCategory;

  /** потребность, объект закупки, это копия потребности сделанная на момент создания тендера */
  @serializeSimplified
  demand: Demand | null;

  /** Желаемые условия оплаты */
  desiredPaymentTerms: PaymentTerm[];

  /** Валюта */
  private _priceStartCurrencyCode: string;

  /** стартовая цена полная */
  @Title_ru_RU('начальная цена', TYPES.PRICE)
  private _priceStartFull: Price;

  /** Начало и конец торгов */
  private _startAt: TimeStamp;
  private _endAt: TimeStamp;

  /** Планировалось: каждая ставка может быть перебита только более низкой ценой и последней всегда
   будет самой низкой. Но появилась потребность в отмене ставки и возможность принимать любую цену - что бы
   весь требуемый объём в тендере купить от разных продавцов */
  @serializeSimplified
  private _bestBid: TenderBid;

  /** текущая ставка партнёра */
  @serializeSimplified
  private _currentPartnerBid: TenderBid;

  /** Для покупателя, все ставки, для поставщика, только его */
  bids: TenderBid[];

  /** Ссылка на позиции заказов, появится когда тендер завершится.
   *  Удалённые позиции остаются здесь: ссылка на заказ должна быть видна
   *  и после его удаления. Для расчётов и запретов — activeOrderPositions */
  orderPositions: any[];

  changes: FeedRecord[];

  /** День к которому покупатеь хочет получать товар */
  desiredReceiptDate: DateSimple | undefined;

  /** --------------- Фронтовские поля ---------------- */
  /** Поле чисто фронтовское, нужно на этапе создания сущности,
   * выбрано по умолчанию, что тендер по умолчанию стартует через 20 минут
   * */
  isCreationDelayMode: boolean;

  @Title_ru_RU('текущая цена', TYPES.PRICE)
  priceCurrent = null;

  /** Все возможные участники, сделавшие ставку, отказавшиеся, ещё не определившиеся **/
  _participants: Array<ParticipationDetailProduct> | null = null;

  /** Заявки на участие в тендере **/
  participationRequests: Array<TenderParticipationRequest> | null = null;

  /** Приходит только для вкладки «Аналитика тендеров» */
  suppliersStats: TenderSuppliersStats | null = null;

  static fromServer(tenderData: any, result = new Tender()) {
    super.fromServer(tenderData, result);

    result.desiredPaymentTerms = Core.utils.hydrateArray(tenderData.desiredPaymentTerms, PaymentTerm.fromServer);

    result.winnersCategory = Core.utils.hydrate(tenderData.winnersCategory, WinnersCategory.fromServer)!;

    result.desiredReceiptDate = DateSimple.fromServer(tenderData.desiredReceiptDate)!

    result._startAt = TimeStamp.fromServer(tenderData.startAt)!;
    result._endAt = TimeStamp.fromServer(tenderData.endAt)!;

    result._priceStartFull = Price.fromServer(tenderData.priceStartFull);

    result.bids = Core.utils.hydrateArray(tenderData.bids, TenderBid.fromServer);
    result.bids.forEach(_bid => _bid.tender = result)

    result.changes = Core.utils.hydrateArray(tenderData.changes, FeedRecord.fromServer);
    result.orderPositions = tenderData.orderPositions.map((orderPosition: any) => orderPosition.isPreorder ? PreorderPosition.fromServer(orderPosition) : OrderPosition.fromServer(orderPosition));

    result.demand = Core.utils.hydrate(tenderData.demand, Demand.fromServer);

    result.suppliersStats = Core.utils.hydrate(tenderData.suppliersStats, TenderSuppliersStats.fromServer);

    return result;
  }

  static fromDemand(demand: Demand): Tender {
    const result = new this();

    if (!Core.utils.isObject(demand.productCategory)) {
      Core.error('Потребность с не распределенным товаром - нельзя создать тендер');
      Core.showNotification.error('Потребность с не распределенным товаром - нельзя создать тендер');
    }

    /** Отсрочка категории приходит с потребностью — demands.get отдаёт её вместе с категорией */
    const productCategory = demand.productCategory;

    result.buyer            = Core.currentPartner!;
    result.responsibleBuyer = Core.currentUser!;

    result.demand = demand;

    /** Сеттер product заодно проставляет productCategory и unit из товара.
     *  Ниже добираем из потребности то, чего у товара не оказалось. */
    result.product = demand.product;

    if (!result.unit) result.unit = demand.unit;

    result.winnersCategory = TendersService.WINNERS_CATEGORIES.get(1);
    result.productCategory = productCategory;

    result._priceStartFull = Price.zero();

    /** Бэк не принимает desiredPaymentTerms равным null — ставим отсрочку
     *  категории от события получения. */
    result.desiredPaymentTerms = [new PaymentTerm(
      PaymentTermsService.getEventById('after_receiving'),
      productCategory.paymentDelay,
    )];

    result.isPreorderNeeded       = demand.isPreorderNeeded;
    result.preorderMonthlyReserve = demand.preorderMonthlyReserve;
    result.preorderShipmentPeriod = demand.preorderShipmentPeriod;
    result.desiredReceiptDate     = demand.desiredReceiptDate;

    result.qtyDesired = demand.qtyLeft;

    result.isCreationDelayMode = TendersService.config.IS_CREATION_DELAY_MODE;
    result._startAt            = new TimeStamp();

    if (result.isCreationDelayMode === true) {
      result.applyCreationDelay();
    }


    return result;
  }

  static fromDemandWithFlexibleQty(demand: Demand): Tender {
    const result = this.fromDemand(demand);

    /** Гибкое количество задаётся категорией победителей — здесь она не
     *  дефолтная, поэтому ставится явно, а не источником формы. */
    result.winnersCategory = TendersService.WINNERS_CATEGORIES.get(3);

    result.desiredReceiptDate = undefined;

    return result;
  }

  /**
   * Копирует в этот тендер общие поля из образца. Обратная операция форме:
   * пользователь заполнил один, остальные равняются по нему.
   */
  applySharedFieldsFrom(source: Tender): void {
    Tender.SHARED_FIELDS.forEach((field) => {
      this[field] = source[field];
    });
  }

  /**
   * Ставит начало торгов через фиксированную задержку от текущего момента.
   * Зовётся непосредственно перед отправкой: посчитанный заранее момент к
   * запросу уже уедет.
   */
  applyCreationDelay(): void {
    this._startAt = new TimeStamp().addMilliseconds(TendersService.config.CREATION_DELAY_MS);
  }

  get name() {
    /** Не очень правильно в модели ссылаться на текущего поставщика, по идее
     * Lion изнутри своей ничего про sheep знать не должен, откуда бы она там появилась
     * Но с другой стороны это KISS и прагматично
     **/
    return `${this.entityTypeName} ${(Core.currentPartner!.isSeller ? this.productCategory.name : (this.product?.name ?? this.productCategory.name)).toLowerCase()}`;
  }

  get productCode() {
    return this?.product?.code || '--';
  }

  /**
   * Первая часть желаемого платежа.
   *
   * Переходный фасад: хранение уже списком, но частей всегда ровно одна, а
   * формы и расчёты написаны под одно условие. Когда частей станет больше,
   * всё, что ходит сюда, придётся переводить на desiredPaymentTerms.
   */
  set desiredPaymentTerm(value: PaymentTerm) {
    this.desiredPaymentTerms = [value];
  }

  get desiredPaymentTerm(): PaymentTerm {
    return this.desiredPaymentTerms[0];
  }

  @Title_ru_RU('начальная цена', TYPES.PRICE)
  set priceStartFull(value) {
    this._priceStartFull = value
  }

  get priceStartFull(): Price {
    return this._priceStartFull
  }

  get hasBids(): boolean | null {
    return Core.utils.isNotEmptyArray(this.bids);
  }

  @Title_ru_RU('лидирующая ставка', TYPES.TENDER_BID)
  get bestBid(): TenderBid | null {
    if (!this._bestBid) return null;
    return this.bids.find(_bid => this._bestBid.id === _bid.id) || null;
  }

  get yourLastBid(): TenderBid | null {
    if (!this.hasBids) return null;

    return this.bids.findLast(bid => bid.isMine) || null;
  }

  get isAllowSecondBestBid(): boolean {
    if (!this.winnersCategory?.id) return false;

    return this.winnersCategory.id === 2;
  }

  get isRunning(): boolean | null {
    if (!this._startAt || !this._endAt) return null;
    return this._startAt.isBefore(Core.liveTime.toDateTime()) && this._endAt.isAfter(Core.liveTime.toDateTime());
  }

  get isFinished(): boolean | null {
    if (!this._endAt) return null;

    return Core.liveTime.isAfter(this._endAt);
  }

  get isNotStarted(): boolean | null {
    if (!this._startAt) return null;
    return Core.liveTime.isBefore(this._startAt);
  }

  get isStarted(): boolean | null {
    if (!this._startAt) return null;

    return Core.liveTime.isAfter(this._startAt);
  }

  get timeUntilEnd(): number | null {
    if (!this._endAt) return null;

    return this.endAt.valueOf() - Core.liveTime.valueOf();
  }

  get timeUntilStart(): number {
    return this.startAt.valueOf() - Core.liveTime.valueOf();
  }

  /**
   * Код статуса тендера — тот же набор значений, что принимает параметр
   * status в tenders.getList и что показывает фильтр колонки.
   *
   * isOrdersCreated проверяется раньше isFinished: заказ создаётся
   * по завершённому тендеру, и без этого порядка «Создан заказ»
   * никогда бы не выиграл у «Ожидает заказа».
   */
  get statusId(): string | null {
    if (this.isNotStarted) return TendersService.STATUS.NOT_STARTED;
    if (this.isRunning) return TendersService.STATUS.RUNNING;
    if (this.isOrdersCreated) return TendersService.STATUS.ORDER_CREATED;
    if (this.isFinished) return TendersService.STATUS.READY_FOR_ORDER;
    return null;
  }

  get status(): string {
    if (this.isDeleted) return 'Удалён';
    if (this.statusId) return TendersService.STATUS_NAMES[this.statusId];
    return '--';
  }

  get timeRemainingBeforeNextEvent(): string {
    if (this.isFinished) {
      return `${Core.utils.date.format(this.endAt, 'DD|MM|yyyy|HH|mm')}`
    }

    if (this.isRunning) {
      if (this.timeUntilEnd as number > Core.utils.date.ONE_DAY) {

        return `${Core.utils.date.format(this.endAt, 'DD|MM|yyyy|HH|mm')}`

      } else {
        const {hours, minutes, seconds} = Core.utils.date.formatDuration(this.timeUntilEnd!)
        return `${hours}:${minutes}:${seconds}`
      }
    }

    if (this.isNotStarted) {
      if (this.timeUntilStart as number > Core.utils.date.ONE_DAY) {

        return `Начнётся ${Core.utils.date.format(this.startAt, 'DD|MM|yyyy|HH|mm')}`

      } else {
        const {hours, minutes, seconds} = Core.utils.date.formatDuration(this.timeUntilStart)
        return `Начнётся через ${hours}:${minutes}:${seconds}`
      }
    }

    return '--:--:--';
  }

  isBestBid(bid: TenderBid): boolean {
    if (!this.bestBid) return false;

    return this.bestBid.id === bid.id;
  }

  isSecondBestBid(bid: TenderBid): boolean {
    if (this.bids.length < 2) return false;

    /** мне приходит уже отсортированный список по цене */
    return bid.id === this.bids[1].id;
  }

  /** У текущего продавца уже есть ставка — можно только понизить цену. */
  get hasOwnBid(): boolean {
    return this.yourLastBid !== null;
  }

  /**
   * По тендеру есть удалённые позиции заказов. На бэке тому же признаку
   * отвечает positions_failed_count, по которому тендер отбирается в
   * «Создан заказ»: заказ был и его удалили, дозакупать по такому тендеру
   * уже не будут.
   * Источник - https://trello.com/c/IbsFSTvv
   */
  get hasFailedOrderPositions(): boolean {
    if (Core.utils.isNotEmptyArray(this.orderPositions) === false) return false;

    return this.orderPositions.some(_pos => _pos.isDeleted === true);
  }

  /**
   * Позиции заказов, существующие сейчас. Всё, что считает количество или
   * запрещает действие, работает с ними: удалённые позиции лежат в
   * orderPositions только ради ссылки на заказ в карточке.
   */
  get activeOrderPositions(): any[] {
    if (Core.utils.isNotEmptyArray(this.orderPositions) === false) return [];

    return this.orderPositions.filter(_pos => _pos.isDeleted === false);
  }

  get hasOwnOrderPositions(): boolean {
    if (Core.utils.isEmptyArray(this.orderPositions)) return false;
    // Геттер только для поставщиков. orderPosition не носит поле buyer, нельзя сказать с кем из покупателей заключен заказ
    if (Core.currentPartner.isBuyer === true) return null;
    return this.orderPositions.some(_pos => _pos.seller.id === Core.currentPartner.id)
  }

  /**
   * Продавцу нужно разрешение, а записи о нём нет: ни одобрения, ни отказа.
   * Пока так — ставку сделать нельзя, надо подавать заявку.
   */
  get needsPermission(): boolean {
    return this.hasAnySupplyOfferByCurrentPartner === false
      && this.isEveryoneCanBid === false;
  }

  /** Текущий партнёр вправе сделать ставку. */
  get canParticipate(): boolean {
    if (this.isEveryoneCanBid === true) return true;

    // у поставщика есть supplyOffer.isApproved === true && supplyOffer.isExcludedFromTenders === false
    if (this.hasApprovedAndNotExcludedSupplyOfferByCurrentPartner === true) return true;

    return false;
  }

  @Title_ru_RU('текущая ставка партнёра', TYPES.PRICE)
  get currentPartnerBid(): TenderBid | null {
    if (!this._currentPartnerBid?.id) return null;

    return this.bids.find(_bid => this._currentPartnerBid.id === _bid.id) || null;
  }

  @Title_ru_RU('валюта стартовой цены', TYPES.STRING)
  set priceStartCurrencyCode(value: string) {
    this._priceStartCurrencyCode = value;
  }

  get priceStartCurrencyCode(): string {
    return this._priceStartCurrencyCode
  }

  @Title_ru_RU('начало торгов', TYPES.DATE_TIME)
  set startAt(value: TimeStamp | Date | string) {
    this._startAt = TimeStamp.from(value)
  }

  get startAt(): TimeStamp {
    return this._startAt
  }

  @Title_ru_RU('окончание торгов', TYPES.DATE_TIME)
  set endAt(value: TimeStamp | Date | string) {
    this._endAt = TimeStamp.from(value)
  }

  get endAt(): TimeStamp {
    return this._endAt
  }

  /**
   * Количество в тендере — ориентировочное, не обязательное.
   * Покупатель может заказать больше или меньше.
   * Используется для запроса котировок без обязательства выкупа фиксированного объёма.
   * Если категория победителей не задана — флаг false.
   */
  get isQtyOrderedFlexible(): boolean {
    return this.winnersCategory?.id === 3 || this.winnersCategory?.id === 4;
  }

  /**
   * Реквизиты правятся, пока торги не начались. У несозданного тендера
   * ограничения нет: «начался» про него сказать нечего.
   */
  get canChangeRequisites(): boolean {
    if (this.isExisting === false) {
      return true;
    }
    else if (this.isStarted === true) {
      return false;
    }
    else {
      return true;
    }
  }

  /**
   * Почему нельзя отменить
   */
  getReasonCannotCancelByCurrentUser(): string | null {
    if (Core.utils.isNullOrUndefined(Core.currentPartner) === true) return 'Пользователь не авторизован';
    if (Core.utils.isNullOrUndefined(this.id) === true) return TendersService.UNAVAILABILITY_REASONS.NO_EXIST_ENTITY_ON_BACKEND;
    if (Core.currentPartner.isSeller === true) return TendersService.UNAVAILABILITY_REASONS.IS_SELLER;
    if (this.isFinished === false) return TendersService.UNAVAILABILITY_REASONS.IS_NOT_FINISHING;
    /** Удалённые позиции не считаем: заказа по ним больше нет, в orderPositions
     *  они остались только ради ссылки в карточке. Иначе их количество попадёт
     *  в сумму и тендер объявится исчерпанным при живом заказе на часть объёма. */
    if (Core.utils.isNotEmptyArray(this.activeOrderPositions) === false) {
      return TendersService.UNAVAILABILITY_REASONS.ORDERS_IS_NONE;
    } else {
      /** нельзя закрыть уже исчерпанный тендер **/
      const qtyOrdered = this.activeOrderPositions.reduce((acc: Qty, item) => acc.plus(item.qty), Qty.zero());

      if (qtyOrdered.greaterThan(this.qtyDesired) === true || qtyOrdered.equals(this.qtyDesired)) {
        return TendersService.UNAVAILABILITY_REASONS.TENDERS_WAS_EXHAUST;
      }
    }

    return null;
  }

  /**
   * Почему сравнение сейчас недоступно
   */
  getReasonCannotCompareWithDemandByCurrentUser(): string | null {
    if (Core.utils.isNullOrUndefined(Core.currentPartner) === true) return 'Пользователь не авторизован';
    if (Core.utils.isNullOrUndefined(this.id) === true) return TendersService.UNAVAILABILITY_REASONS.NO_EXIST_ENTITY_ON_BACKEND;
    if (Core.currentPartner.isSeller === true) return TendersService.UNAVAILABILITY_REASONS.IS_SELLER;
    if (this.isStarted === false) return TendersService.UNAVAILABILITY_REASONS.IS_NOT_STARTED;
    if (this.isDeleted === true) return TendersService.UNAVAILABILITY_REASONS.IS_DELETED;
    if (Core.utils.isEmptyArray(this.bids) === true) return 'Ставок нет';
    return null;
  }


  /**
   * Можем ли посмотреть заказ?
   */
  getReasonCannotShowOrderByCurrentUser(): string | null {
    if (Core.utils.isNullOrUndefined(Core.currentPartner) === true) return 'Пользователь не авторизован';
    if (Core.utils.isNullOrUndefined(this.id) === true) return TendersService.UNAVAILABILITY_REASONS.NO_EXIST_ENTITY_ON_BACKEND;
    if (Core.utils.isNotEmptyArray(this.orderPositions) === false) return TendersService.UNAVAILABILITY_REASONS.ORDERS_IS_NONE;

    /** Если текущий пользователь поставщик, нужно сделать проверку */
    if (Core.currentPartner.isSeller === true) {
      /** Если заказ есть, но не относится к текущему покупателю, то до свидания */
      if (this.hasOwnOrderPositions === false) {
        return TendersService.UNAVAILABILITY_REASONS.IS_SELLER;
      }
    }

    return null;
  }

  /**
   * Можем ли сделать ставку?
   */
  getReasonCannotCreateBidByCurrentUser(): string | null {
    if (Core.utils.isNullOrUndefined(Core.currentPartner) === true) return 'Пользователь не авторизован';
    if (Core.currentPartner.isBuyer === true) return TendersService.UNAVAILABILITY_REASONS.IS_BUYER;
    if (Core.currentPartner.isSeller === true && this.isRunning === false) return TendersService.UNAVAILABILITY_REASONS.IS_NOT_RUNNING;
    if (this.isFinished === true) return TendersService.UNAVAILABILITY_REASONS.IS_FINISHED;
    // Если пользователь отказался, то вообще-то блокировать создание ставки ему не надо, потому что а зачем?
    // if (this.isRefusedTenderByCurrentPartner === true) return TendersService.UNAVAILABILITY_REASONS.IS_REFUSED;
    if (this.isEveryoneCanBid === true) return null;
    if (this.isEveryoneCanBid === null) return TendersService.UNAVAILABILITY_REASONS.USE_TENDER_CARD;
    if (this.hasApprovedSupplyOfferByCurrentPartner === false) return TendersService.UNAVAILABILITY_REASONS.IS_NOT_APPROVED_SELLER;
    if (this.hasApprovedAndNotExcludedSupplyOfferByCurrentPartner === false) return TendersService.UNAVAILABILITY_REASONS.IS_EXCLUDED_FROM_TENDERS;

    return null;
  }


  /**
   * Можем удалить ставку?
   */
  getReasonCannotDeleteOwnBidByCurrentUser(): string | null {
    if (Core.utils.isNullOrUndefined(Core.currentPartner) === true) return 'Пользователь не авторизован';
    if (Core.utils.isNullOrUndefined(this.id) === true) return TendersService.UNAVAILABILITY_REASONS.NO_EXIST_ENTITY_ON_BACKEND;
    if (Core.currentPartner.isBuyer === true) return TendersService.UNAVAILABILITY_REASONS.IS_BUYER;
    if (this.isFinished === true) return TendersService.UNAVAILABILITY_REASONS.IS_FINISHED;
    if (this.yourLastBid === null) return TendersService.UNAVAILABILITY_REASONS.NO_EXIST_BID;

    return null;
  }

  /**
   * Можем удалить ставку?
   */
  getReasonCannotDeleteTenderByCurrentUser(): string | null {
    // note bene! f/5075 можно удалять тендеры даже если идут торги, удалил блок с проверкой на isRunning
    if (Core.utils.isNullOrUndefined(Core.currentPartner) === true) return 'Пользователь не авторизован';
    if (Core.utils.isNullOrUndefined(this.id) === true) return TendersService.UNAVAILABILITY_REASONS.NO_EXIST_ENTITY_ON_BACKEND;
    if (Core.currentPartner.isSeller === true) return TendersService.UNAVAILABILITY_REASONS.IS_SELLER;
    /** Удалённый заказ удалению тендера не мешает */
    if (Core.utils.isNotEmptyArray(this.activeOrderPositions) === true) return TendersService.UNAVAILABILITY_REASONS.HAS_ORDER;
    if (this.isDeleted === true) return TendersService.UNAVAILABILITY_REASONS.IS_DELETED;

    return null;
  }

  /**
   * Можем ли указать причину неучастия?
   */
  getReasonCannotSetRefusalReasonsByCurrentUser(): string | null {
    if (Core.utils.isNullOrUndefined(Core.currentPartner) === true) return 'Пользователь не авторизован';
    if (this.isEveryoneCanBid === true) return TendersService.UNAVAILABILITY_REASONS.IS_EVERYONE_CAN_BID;
    if (this.isEveryoneCanBid === null) return TendersService.UNAVAILABILITY_REASONS.USE_TENDER_CARD;
    if (this.hasApprovedSupplyOfferByCurrentPartner === false) return TendersService.UNAVAILABILITY_REASONS.IS_NOT_APPROVED_SELLER;
    if (this.hasApprovedAndNotExcludedSupplyOfferByCurrentPartner === false) return TendersService.UNAVAILABILITY_REASONS.IS_EXCLUDED_FROM_TENDERS;
    if (this.currentPartnerBid !== null) return TendersService.UNAVAILABILITY_REASONS.YOU_ARE_PARTICIPATING
    if (this.isFinished === true) return TendersService.UNAVAILABILITY_REASONS.IS_FINISHED;

    return null;
  }

  /**
   * Можем ли согласиться на участие?
   *
   * Согласие снимает ранее проставленный отказ — без отказа снимать нечего.
   */
  getReasonCannotAgreeParticipateByCurrentUser(): string | null {
    if (Core.utils.isNullOrUndefined(Core.currentPartner) === true) return 'Пользователь не авторизован';
    if (Core.utils.isNullOrUndefined(this.id) === true) return TendersService.UNAVAILABILITY_REASONS.NO_EXIST_ENTITY_ON_BACKEND;
    if (Core.currentPartner.isBuyer === true) return TendersService.UNAVAILABILITY_REASONS.IS_BUYER;
    if (this.isFinished === true) return TendersService.UNAVAILABILITY_REASONS.IS_FINISHED;
    if (this.hasOwnBid === true) return TendersService.UNAVAILABILITY_REASONS.YOU_ARE_PARTICIPATING;
    if (this.isRefusedTenderByCurrentPartner === null) return TendersService.UNAVAILABILITY_REASONS.USE_TENDER_CARD;
    // Если ты не отклонял участие в тендере, то и согласиться на участие ты не можешь
    if (this.isRefusedTenderByCurrentPartner === false) return TendersService.UNAVAILABILITY_REASONS.IS_NOT_REFUSED;
    if (this.hasApprovedAndNotExcludedSupplyOfferByCurrentPartner === false) return TendersService.UNAVAILABILITY_REASONS.IS_EXCLUDED_FROM_TENDERS;

    return null;
  }

  /**
   * Можем ли изменить причину неучастия?
   *
   * Правится уже проставленный отказ, поэтому он должен быть.
   */
  getReasonCannotChangeRefusalReasonsByCurrentUser(): string | null {
    if (Core.utils.isNullOrUndefined(Core.currentPartner) === true) return 'Пользователь не авторизован';
    if (Core.utils.isNullOrUndefined(this.id) === true) return TendersService.UNAVAILABILITY_REASONS.NO_EXIST_ENTITY_ON_BACKEND;
    if (Core.currentPartner.isBuyer === true) return TendersService.UNAVAILABILITY_REASONS.IS_BUYER;
    if (this.isFinished === true) return TendersService.UNAVAILABILITY_REASONS.IS_FINISHED;
    if (this.hasOwnBid === true) return TendersService.UNAVAILABILITY_REASONS.YOU_ARE_PARTICIPATING;
    if (this.isRefusedTenderByCurrentPartner === null) return TendersService.UNAVAILABILITY_REASONS.USE_TENDER_CARD;
    if (this.isRefusedTenderByCurrentPartner === false) return TendersService.UNAVAILABILITY_REASONS.IS_EVERYONE_CAN_BID;
    if (this.hasApprovedAndNotExcludedSupplyOfferByCurrentPartner === false) return TendersService.UNAVAILABILITY_REASONS.IS_EXCLUDED_FROM_TENDERS;

    return null;
  }

  /** Можем ли создать заказ? */
  getReasonCannotCreateOrderByCurrentUser(): string | null {
    if (Core.utils.isNullOrUndefined(Core.currentUser) === true) return 'Пользователь не авторизован';
    if (this.isFinished === false) return TendersService.UNAVAILABILITY_REASONS.IS_NOT_FINISHING;
    return null;
  }

  /** Может ли пользователь отправить заявку на участие */
  getReasonCannotSendParticipationRequest(): string | null {
    if (Core.utils.isNullOrUndefined(Core.currentPartner) === true) return 'Пользователь не авторизован';
    if (Core.currentPartner.isBuyer === true) return TendersService.UNAVAILABILITY_REASONS.IS_BUYER;
    if (this.isFinished === true) return TendersService.UNAVAILABILITY_REASONS.IS_FINISHED;
    if (this.isEveryoneCanBid === true) return TendersService.UNAVAILABILITY_REASONS.PERMISSION_NOT_NEEDED;

    if (this.hasAnySupplyOfferByCurrentPartner === true) {
      if (this.isRefusedTenderByCurrentPartner === true) {
        return TendersService.UNAVAILABILITY_REASONS.IS_REFUSED;
      }

      /** Разрешений может быть несколько. Отказ показываем только когда
       *  не осталось ни одного одобренного: одно живое разрешение означает,
       *  что участвовать можно и заявка не нужна. */
      if (this.hasNoApprovedSupplyOfferByCurrentPartner === true) {
        return TendersService.UNAVAILABILITY_REASONS.PERMISSION_DECLINED;
      }

      if (this.hasApprovedAndNotExcludedSupplyOfferByCurrentPartner === false) {
        return TendersService.UNAVAILABILITY_REASONS.IS_EXCLUDED_FROM_TENDERS;
      }

      return TendersService.UNAVAILABILITY_REASONS.ALREADY_HAS_PERMISSION;
    }

    if (this.hasParticipationRequestByCurrentPartner === true) return TendersService.UNAVAILABILITY_REASONS.ALREADY_SENT_REQUEST;

    /* DAO метод в любом случае вернет массив, хоть и пустой, поэтому можно смотреть на null, если null,
       значит, что participationRequests мы еще не получали и не можем определить может ли пользователь
       отправить запрос на участие */
    if (this.participationRequests === null) return TendersService.UNAVAILABILITY_REASONS.USE_TENDER_CARD;

    return null;
  }


  /** ---------------------- Функционал, связанный с участием в тендере ---------------------- **/
  get participants() {
    return this._participants;
  }

  set participants(value: Array<ParticipationDetailProduct>) {
    this._participants = value;
  }

  /**
   * Подгружает участников — одобренных и отказавшихся. Отдельный запрос:
   * в tenders.get они не приходят.
   */
  async loadParticipants(): Promise<void> {
    this.participants = await TendersService.getParticipation(this.id);
  }

  /** Подгружает заявки на участие. Тоже отдельный запрос. */
  async loadParticipationRequests(): Promise<void> {
    this.participationRequests = await TenderParticipationRequestsService.getList(this.id);
  }

  /** Ни одного одобренного разрешения у текущего поставщика. */
  get hasNoApprovedSupplyOfferByCurrentPartner(): boolean {
    return Core.utils.isEmptyArray(this.product.getApprovedSupplyOffersByPartner(Core.currentPartner));
  }

  /** Есть ли какая-то запись о возможности (или не возможности) сделать ставку **/
  get hasAnySupplyOfferByCurrentPartner(): boolean {
    return Core.utils.isNotEmptyArray(this.product.getSupplyOffersByPartner(Core.currentPartner));
  }

  /** Имеет ли текущий поставщик разрешение играть **/
  get hasApprovedSupplyOfferByCurrentPartner(): boolean {
    return Core.utils.isNotEmptyArray(this.product.getApprovedSupplyOffersByPartner(Core.currentPartner));
  }

  /** Имеет ли текущий поставщик разрешение играть **/
  get hasApprovedAndNotExcludedSupplyOfferByCurrentPartner(): boolean {
    return Core.utils.isNotEmptyArray(
      this.product.getApprovedAndNotExcludedSupplyOffersByPartner(Core.currentPartner),
    );
  }

  /** Помимо обычных записей SupplyOffer, есть ещё заявки на участие **/
  get hasParticipationRequestByCurrentPartner(): boolean {
    if (!this.participationRequests) return false;
    return this.participationRequests.some(_item => _item.partner.id === Core.currentPartner.id)
  }

  /**
   * Если нет ни одного supplyOffer по этому тендеру, то разрешено участие всем.
   *
   * В tenders.getList счётчик не приходит, а разрешения у поставщика только
   * свои: своя запись значит, что разрешения на товар есть, без неё — неизвестно.
   */
  get isEveryoneCanBid(): boolean | null {
    if (Core.utils.isNullOrUndefined(this.product.supplyOffersCount) === false) {
      return this.product.supplyOffersCount === 0;
    }
    else if (Core.utils.isNotEmptyArray(this.product.actualSupplyOffers) === true) {
      return false;
    }
    else {
      return null;
    }
  }

  /**
   * Записи о текущем партнёре как об участнике тендера.
   *
   * Их несколько: у поставщика может быть по разрешению на каждое юр. лицо
   * или производителя, и на каждое заводится своя запись участия. Состояние
   * у них общее — отказ и его причина проставляются во все сразу, поэтому
   * для отображения годится первая.
   */
  get participationDetailProductsByCurrentPartner(): ParticipationDetailProduct[] {
    if (Core.currentPartner!.isBuyer) return [];
    if (!this._participants) return [];

    return this._participants.filter(
      (item: ParticipationDetailProduct) => item.supplyOffer.seller.id === Core.currentPartner!.id,
    );
  }

  /**
   * Участия текущего партнёра, по которым он вправе играть: разрешение не
   * исключено из торгов. Отказ и согласие проставляются только в них —
   * по исключённому разрешению отказываться не от чего.
   */
  get participationDetailProductsNotExcludedByCurrentPartner(): ParticipationDetailProduct[] {
    return this.participationDetailProductsByCurrentPartner.filter(
      (item: ParticipationDetailProduct) => item.supplyOffer.isExcludedFromTenders === false,
    );
  }

  /** Текущий партнёр отклонил участие в этом тендере? В реестре участия не приходят — null **/
  get isRefusedTenderByCurrentPartner(): boolean | null {
    if (Core.currentPartner!.isBuyer) return false;
    if (this._participants === null) return null;

    const participations = this.participationDetailProductsNotExcludedByCurrentPartner;
    if (Core.utils.isEmptyArray(participations)) return false;

    return participations[0].participation === false;
  }

  /** Все кто отказались **/
  get nonParticipants() {
    if (!this._participants) return [];
    return this._participants.filter(_item => _item.participation === false) || [];
  }

  /** Все кто не принял участие, и/или планирует принять участие **/
  get undecidedParticipants() {
    if (!this._participants) return [];
    return this._participants.filter(_item =>
      _item.participation === null && _item.supplyOffer.isApproved
    ) || [];
  }

  /**
   * Получить доступные количества для заказа с учётом фасовки
   */
  getAvailableQtysForOrder(packageVolume: Qty): Record<string, Qty> {
    if (this.demand?.qtyLeft.isPositive() !== true) return {};

    /** Почему Number.isInteger(this.demand.qtyLeft)?
     * Потому что представим что у нас два поставщика,
     * один с дробной фасовкой, другой с целочисленной.
     * Если пользователь заказал у поставщика с дробной фасовкой,
     * то this.demand.qtyLeft становится числом с плавающей точкой.
     * Из этого следует что если шаг будет целочисленный, то мы никогда
     * не найдём ближайшее большее кратное
     **/
    // const step =
    // 				Number.isInteger(packageVolume)
    // 				&& Number.isInteger(this.demand.qtyLeft)
    // 					? 1
    // 					: 0.001;


    /** Решил отказаться от затей просчитывать шаг, слишком много нужно учесть чтобы
     * попасть, вероятность ошибиться высокая, выйгрышь незначителен, риск не оправдан */

    // Если победителя 2 и ничего не заказано - делим 70/30
    // note bene! это имеет смысл, только если игроков больше одного
    if (this.winnersCategory?.id === 2 && this.demand.qtyOrdered.isZero()) {
      return this.calculateSplitQuantities(packageVolume);
    }

    // Обычный случай - весь остаток
    return this.calculateRemainingQuantity(packageVolume);
  }

  /**
   * Рассчитать разделённые количества для двух победителей (70/30)
   */
  private calculateSplitQuantities(packageVolume: Qty): Record<string, Qty> {
    /** Доля первого победителя — 70% остатка. Не через Multiplier: тот выражает наценку,
     *  и 70% пришлось бы писать как fromPercent(-30) */
    const highQtyTarget = this.demand.qtyLeft.percentage(70);

    const highQty = highQtyTarget.ceilToMultiple(packageVolume);
    const lowQty = this.demand.qtyLeft.minus(highQty);

    const highQtyPercent = highQty.percentOf(this.demand.qtyDesired);
    const lowQtyPercent = lowQty.percentOf(this.demand.qtyDesired);

    return {
      [`${highQtyPercent}%`]: highQty,
      [`${lowQtyPercent}%`]: lowQty
    };
  }

  /**
   * Рассчитать оставшееся количество
   */
  private calculateRemainingQuantity(packageVolume: Qty): Record<string, Qty> {
    const remainingQty = this.demand.qtyLeft.ceilToMultiple(packageVolume);

    const remainingQtyPercent = remainingQty.percentOf(this.demand.qtyDesired);

    let label: string;

    if (this.demand.qtyOrdered.isPositive()) {
      label = `остаток ${remainingQtyPercent}%`;
    } else {
      label = `${remainingQtyPercent}%`;
    }

    return {[label]: remainingQty};
  }

  toString(): string {
    return this.name;
  }
}

export default Tender;
